import { useMemo } from "react";
import { useTermIndex } from "./useTermIndex";
import { usePlaceMatches } from "./usePlaceMatches";
import { useInterpretedTerms } from "./useInterpretedTerms";
import { DEFAULT_RELATED_CAP } from "../lib/search/matchTerms";
import { rankTerms } from "../lib/search/rankTerms";

// Three character-count tiers: below this, matching runs in "prefix" mode (its own header + a
// higher related cap) instead of "exact" mode -- see matchTerms.js and CommandPalette.js.
const MIN_CHARS_FOR_EXACT_MODE = 3;
const PREFIX_MODE_RELATED_CAP = 8;

/**
 * Everything the command palette needs for one typed fragment: synchronous metric/filter matches
 * (useTermIndex + matchTerms) merged with async place matches (usePlaceMatches), split into
 * exact/related the same way for both sources. Below `MIN_CHARS_FOR_EXACT_MODE`, both sources
 * switch to prefix matching and a higher related cap (`mode: "prefix"`, surfaced so
 * CommandPalette can label the header and treat Zone 1 as always-shown/default-highlighted).
 *
 * `placeFragment` is deliberately a separate, wider string from `fragment` (see useSegments.js's
 * `activePlaceFragment`) -- place names are usually multi-word, and the single-word `fragment`
 * that's exactly right for term/filter matching silently misranks or breaks a place lookup (a
 * real bug: "virginia" alone ranks plain "Virginia" over "West Virginia"). Classifying a returned
 * place as exact-vs-related below still compares against `fragment` (the single word actually
 * being typed right now), not `placeFragment` (which only decides what query fetched it).
 */
export function useCommandPaletteMatches(
  fragment,
  placeFragment,
  { relatedCap, interpretText = "", committed } = {}
) {
  const { termIndex, isLoading: isLoadingTermIndex } = useTermIndex();
  const interpretedTerms = useInterpretedTerms(interpretText, termIndex);
  const {
    placeMatches,
    isLoadingPlaces,
    status: placeStatus,
    retry: retryPlaceMatches,
    cancelPending: cancelPlaceMatches,
  } = usePlaceMatches(placeFragment);

  const matches = useMemo(() => {
    const trimmed = (fragment || "").trim();
    const isLoading = isLoadingTermIndex || isLoadingPlaces;

    if (!trimmed) {
      return { exactMatches: [], relatedMatches: [], isLoading, mode: null };
    }

    // `mode` only drives the UI's own header label and related-match cap size now -- matchTerms
    // itself does word-boundary prefix matching at every length (see its own docstring for why).
    const mode = trimmed.length < MIN_CHARS_FOR_EXACT_MODE ? "prefix" : "exact";
    const cap = relatedCap ?? (mode === "prefix" ? PREFIX_MODE_RELATED_CAP : DEFAULT_RELATED_CAP);

    const { exactMatches: rankedExact, relatedMatches, phrase } = rankTerms({
      fragment: trimmed,
      text: interpretText,
      termIndex,
      interpretedTerms,
      relatedCap: cap,
      committed,
    });
    const lower = trimmed.toLowerCase();
    // Same word-boundary prefix check as matchTerms.js, applied to places too -- "west" promoting
    // "West Virginia" (not just burying it in the capped related list) is the same predictiveness
    // ask, just for the place-lookup half instead of the term index.
    const isPrimaryMatch = (displayLower) =>
      displayLower.startsWith(lower) || displayLower.split(/\s+/).some((word) => word.startsWith(lower));

    const mergedExact = [...rankedExact];
    const mergedRelated = [...relatedMatches];

    placeMatches.forEach((place) => {
      if (isPrimaryMatch(place.display.toLowerCase())) {
        mergedExact.push(place);
      } else if (mergedRelated.length < cap) {
        mergedRelated.push(place);
      }
    });

    return { exactMatches: mergedExact, relatedMatches: mergedRelated, isLoading, mode, phrase };
  }, [
    fragment,
    termIndex,
    placeMatches,
    isLoadingTermIndex,
    isLoadingPlaces,
    relatedCap,
    interpretedTerms,
    interpretText,
    committed,
  ]);

  // Place matching's own async status/retry -- surfaced separately from the merged
  // matches above since it's specifically about the place-lookup half, not term/filter matching
  // (which is synchronous and can't time out).
  return { ...matches, placeStatus, retryPlaceMatches, cancelPlaceMatches };
}
